import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterOutputs } from 'shared/api'
import {
	cashCancelReasonKey,
	cashCancelResultKey,
	handoverCancelReasonKey,
	trpc,
	trpcClient,
} from 'shared/api'
import { CheckCircle2 } from 'lucide-react'
import { CompactTable, EmptyTableBody, SectionTitle } from 'shared/components/section'
import { Alert, AlertTitle } from 'shared/components/ui/alert'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'

/**
 * The double payments (ADR-0046; CONTEXT — Double payment): the Recipient paid in
 * cash and the partner's payment (Uzum) arrived too. TezGo recorded the partner's
 * payment inactive; within the Shift the operator resolves it in ADR-0020's order —
 * **step 1** cancels the handover (only when the Parcel was handed over), **step 2**
 * cancels the cash and gives it back, and at that moment the partner's payment is
 * the payment. Then the Parcel is handed over again by its scan.
 *
 * The Point's operator sees it on the handover screen (their own Point), support in
 * its section (every Point — after the Shift it is support's). Which step this Staff
 * member may take now is the server's answer (`steps`); the screen only renders it
 * and names why not. A row disappears once resolved — the cash cancelled, or the
 * partner's payment withdrawn.
 *
 * Shared by the handover screen and the support section; the calls are injected
 * so they are the stubbed tRPC seam in tests. In the hub's look: a section title
 * (the explanation behind its «i»), the cases as one table — the amounts right, in
 * «сум», the steps as buttons in order in the last column.
 */

export type DoublePayment = RouterOutputs['payments']['DoublePayments'][number]
type CancelCashResult = RouterOutputs['payments']['CancelCollected']
type CancelHandoverResult = RouterOutputs['handover']['Cancel']

export interface DoublePaymentCalls {
	cancelHandover: (input: { parcelId: string; reason: string }) => Promise<CancelHandoverResult>
	cancelCash: (input: { parcelId: string; idempotencyKey: string }) => Promise<CancelCashResult>
}

const viaTrpc: DoublePaymentCalls = {
	cancelHandover: (input) => trpcClient.handover.Cancel.mutate(input),
	cancelCash: (input) => trpcClient.payments.CancelCollected.mutate(input),
}

const newCommandKey = () =>
	globalThis.crypto?.randomUUID?.() ?? `double-${Date.now()}-${Math.random().toString(36).slice(2)}`

function sum(amount: number): string {
	return amount.toLocaleString('ru-RU')
}

/** The section: the double payments of the operator's Point, or (support) of every Point. */
export function DoublePayments({ forSupport = false }: { forSupport?: boolean }) {
	const { t } = useTranslate()
	const query = useQuery(trpc.payments.DoublePayments.queryOptions({}))
	const items = query.data ?? []
	// The operator's counter shows the section once there is something to resolve — and
	// keeps it after the last one, to say what to do next.
	const [seen, setSeen] = useState(false)
	if (items.length > 0 && !seen) setSeen(true)
	if (!forSupport && !seen && items.length === 0) return null
	return (
		<section className='flex flex-col gap-2' data-testid='double-payments'>
			<SectionTitle
				title={
					<span className='flex items-center gap-2'>
						{t('doublePayment.title')}
						{items.length > 0 && (
							<Badge variant='destructive' data-testid='double-payments-count'>
								{items.length}
							</Badge>
						)}
					</span>
				}
				hint={t(forSupport ? 'doublePayment.explainSupport' : 'doublePayment.explainOperator')}
			/>
			<DoublePaymentList
				items={items}
				showPoint={forSupport}
				loading={query.isFetching && !query.data}
				onChanged={() => query.refetch()}
			/>
		</section>
	)
}

export interface DoublePaymentListProps {
	items: DoublePayment[]
	/** Name each row's Point (support sees every Point's). */
	showPoint?: boolean
	loading?: boolean
	/** Called after a step went through, to read the list again. */
	onChanged?: () => void
	/** Test seam: the two acts. Defaults to the real tRPC client. */
	calls?: DoublePaymentCalls
}

export function DoublePaymentList({
	items,
	showPoint = false,
	loading = false,
	onChanged,
	calls = viaTrpc,
}: DoublePaymentListProps) {
	const { t } = useTranslate()
	// What the last resolved row asks of the operator next: hand it over again by its scan.
	const [resolved, setResolved] = useState<string | null>(null)

	if (loading) return <div className='text-sm text-muted-foreground'>{t('doublePayment.loading')}</div>
	const columns = showPoint ? 6 : 5
	return (
		<div className='flex flex-col gap-3'>
			{resolved && (
				<Alert data-testid='double-payment-resolved'>
					<CheckCircle2 />
					<AlertTitle>{t('doublePayment.resolved', { code: resolved })}</AlertTitle>
				</Alert>
			)}
			<CompactTable testId='double-payments-table'>
				<TableHeader>
					<TableRow>
						<TableHead>{t('doublePayment.colParcel')}</TableHead>
						<TableHead>{t('doublePayment.colRecipient')}</TableHead>
						{showPoint && <TableHead>{t('doublePayment.colPoint')}</TableHead>}
						<TableHead className='text-right'>{t('doublePayment.colCash')}</TableHead>
						<TableHead className='text-right'>{t('doublePayment.colPartner')}</TableHead>
						<TableHead>{t('doublePayment.colSteps')}</TableHead>
					</TableRow>
				</TableHeader>
				{items.length === 0 ? (
					<EmptyTableBody columns={columns}>
						<span data-testid='double-payments-empty'>{t('doublePayment.empty')}</span>
					</EmptyTableBody>
				) : (
					<TableBody>
						{items.map((item) => (
							<DoublePaymentRow
								key={item.eventId}
								item={item}
								showPoint={showPoint}
								calls={calls}
								onResolved={() => {
									setResolved(item.trackingCode || item.parcelId)
									onChanged?.()
								}}
								onChanged={() => onChanged?.()}
							/>
						))}
					</TableBody>
				)}
			</CompactTable>
		</div>
	)
}

function DoublePaymentRow({
	item,
	showPoint,
	calls,
	onResolved,
	onChanged,
}: {
	item: DoublePayment
	showPoint: boolean
	calls: DoublePaymentCalls
	onResolved: () => void
	onChanged: () => void
}) {
	const { t } = useTranslate()
	const [busy, setBusy] = useState(false)
	const [errorKey, setErrorKey] = useState<string | null>(null)
	const handedOver = item.state === 'HANDED_OVER'
	const { cancelHandover, cancelCash } = item.steps
	const cash = t('payment.som', { amount: sum(item.cash.amount) })

	const doCancelHandover = async () => {
		setBusy(true)
		setErrorKey(null)
		try {
			const res = await calls.cancelHandover({
				parcelId: item.parcelId,
				reason: t('doublePayment.handoverCancelReason'),
			})
			if (res.outcome === 'refused') setErrorKey(handoverCancelReasonKey(res.reason))
			else onChanged()
		} catch {
			setErrorKey('handoverCancel.failed')
		} finally {
			setBusy(false)
		}
	}

	const doCancelCash = async () => {
		if (!globalThis.confirm(t('doublePayment.cashConfirm', { amount: cash }))) return
		setBusy(true)
		setErrorKey(null)
		try {
			const res = await calls.cancelCash({ parcelId: item.parcelId, idempotencyKey: newCommandKey() })
			const refusal = cashCancelResultKey(res)
			if (refusal) setErrorKey(refusal)
			else onResolved()
		} catch {
			setErrorKey('payment.cancelFailed')
		} finally {
			setBusy(false)
		}
	}

	// Step 2 waits for step 1: the server says `handed_over`; the screen says "first step 1".
	const cashReasonKey =
		cancelCash.reason === 'handed_over'
			? 'doublePayment.cashAfterHandover'
			: cancelCash.reason
				? cashCancelReasonKey(cancelCash.reason)
				: null

	return (
		<TableRow data-testid={`double-payment-${item.parcelId}`} data-state={item.state}>
			<TableCell className='align-top'>
				<div className='flex flex-wrap items-center gap-2'>
					<Link to={`/parcels/${item.parcelId}`} className='font-mono underline-offset-4 hover:underline'>
						{item.trackingCode || item.parcelId}
					</Link>
					<Badge variant='outline'>{t(`parcelState.${item.state}` as never)}</Badge>
				</div>
			</TableCell>
			<TableCell className='align-top'>
				<div>{item.recipient.name}</div>
				<div className='text-xs text-muted-foreground'>
					{item.recipient.clientRef}
					{item.partnerName ? ` · ${item.partnerName}` : ''}
				</div>
			</TableCell>
			{showPoint && <TableCell className='align-top'>{item.pointCode}</TableCell>}
			<TableCell className='text-right align-top tabular-nums' data-testid={`double-payment-amounts-${item.parcelId}`}>
				{sum(item.cash.amount)}
			</TableCell>
			<TableCell className='text-right align-top tabular-nums' data-testid={`double-payment-partner-${item.parcelId}`}>
				{item.partnerPayment.currency === 'UZS'
					? sum(item.partnerPayment.amount)
					: `${sum(item.partnerPayment.amount)} ${item.partnerPayment.currency}`}
			</TableCell>
			<TableCell className='align-top whitespace-normal'>
				<div className='flex flex-col gap-1.5 text-sm'>
					{handedOver && (
						<div className='flex items-center gap-2' data-testid={`double-payment-step1-${item.parcelId}`}>
							<span className='shrink-0 text-muted-foreground tabular-nums'>1.</span>
							{cancelHandover.allowed ? (
								<Button
									type='button'
									size='sm'
									variant='outline'
									disabled={busy}
									data-testid={`double-payment-cancel-handover-${item.parcelId}`}
									onClick={doCancelHandover}
								>
									{t('handoverCancel.submit')}
								</Button>
							) : (
								cancelHandover.reason && (
									<span className='text-muted-foreground' data-reason={cancelHandover.reason}>
										{t(handoverCancelReasonKey(cancelHandover.reason) as never)}
									</span>
								)
							)}
						</div>
					)}
					<div className='flex items-center gap-2' data-testid={`double-payment-step2-${item.parcelId}`}>
						{handedOver && <span className='shrink-0 text-muted-foreground tabular-nums'>2.</span>}
						{cancelCash.allowed ? (
							<Button
								type='button'
								size='sm'
								variant='destructive'
								disabled={busy}
								data-testid={`double-payment-cancel-cash-${item.parcelId}`}
								onClick={doCancelCash}
							>
								{t('doublePayment.cancelCash')}
							</Button>
						) : (
							cashReasonKey && (
								<span className='text-muted-foreground' data-reason={cancelCash.reason}>
									{t(cashReasonKey as never)}
								</span>
							)
						)}
					</div>
					{errorKey && (
						<span className='text-destructive' data-testid='double-payment-error' data-key={errorKey}>
							{t(errorKey as never)}
						</span>
					)}
				</div>
			</TableCell>
		</TableRow>
	)
}
